import { useTranslations } from 'next-intl'
import { MapPin, Star, Phone, Mail, Building2, Calendar, Layers, Languages, Clock } from 'lucide-react'
import { type PropertyDetailData, type ReviewStatsData } from '@/services/property-detail'

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  hotel: 'Hotel',
  resort: 'Resort',
  aparthotel: 'Aparthotel',
  boutique: 'Boutique Hotel',
  guesthouse: 'Guesthouse',
  hostel: 'Hostel',
  villa: 'Villa',
  serviced_apartment: 'Serviced Apartment',
  coworking_space: 'Co Working Space',
}

interface PropertyOverviewProps {
  property: PropertyDetailData
  reviewStats: ReviewStatsData | null
  locale: string
}

export function PropertyOverview({ property, reviewStats, locale }: PropertyOverviewProps) {
  const t = useTranslations('booking.propertyDetail')
  const isRtl = locale === 'ar'

  // Pick locale-appropriate description, fallback to generic description
  const descriptionText = isRtl
    ? (property.description_ar || property.description_en || property.description)
    : (property.description_en || property.description)

  return (
    <section>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        {/* Left: Name + Location */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
            {property.name_en}
          </h1>
          {property.name_ar && !isRtl && (
            <p className="text-lg text-gray-500 dark:text-gray-400 mt-0.5" dir="rtl">
              {property.name_ar}
            </p>
          )}

          {property.city && (
            <div className="flex items-center gap-1.5 mt-2 text-gray-600 dark:text-gray-400">
              <MapPin className="h-4 w-4 shrink-0" />
              <span>{property.address || property.city}</span>
            </div>
          )}

          {/* Contact */}
          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
            {property.phone && (
              <a href={`tel:${property.phone}`} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300">
                <Phone className="h-3.5 w-3.5" />
                <span>{property.phone}</span>
              </a>
            )}
            {property.email && (
              <a href={`mailto:${property.email}`} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300">
                <Mail className="h-3.5 w-3.5" />
                <span>{property.email}</span>
              </a>
            )}
          </div>
        </div>

        {/* Right: Rating badge */}
        {reviewStats && reviewStats.totalCount > 0 && (
          <div className="flex items-center gap-3 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg px-4 py-3 shrink-0">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-blue-600 text-white font-bold text-lg">
              {reviewStats.averageRating.toFixed(1)}
            </div>
            <div>
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`h-3.5 w-3.5 ${
                      i < Math.round(reviewStats.averageRating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                {reviewStats.totalCount} {t('reviews')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Property Info Chips */}
      {(property.propertyType || property.checkInTime || property.yearBuilt || property.totalFloors || property.languagesSpoken.length > 0) && (
        <div className="mt-5 flex flex-wrap gap-2">
          {property.propertyType && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300">
              <Building2 className="h-3.5 w-3.5" />
              {PROPERTY_TYPE_LABELS[property.propertyType] ?? property.propertyType}
            </div>
          )}
          {property.checkInTime && property.checkOutTime && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300">
              <Clock className="h-3.5 w-3.5" />
              Check-in {property.checkInTime.slice(0, 5)} / Check-out {property.checkOutTime.slice(0, 5)}
            </div>
          )}
          {property.totalFloors && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300">
              <Layers className="h-3.5 w-3.5" />
              {property.totalFloors} floors
            </div>
          )}
          {property.yearBuilt && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300">
              <Calendar className="h-3.5 w-3.5" />
              Built {property.yearBuilt}
              {property.lastRenovated && property.lastRenovated > property.yearBuilt && ` · Renovated ${property.lastRenovated}`}
            </div>
          )}
          {property.languagesSpoken.length > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300">
              <Languages className="h-3.5 w-3.5" />
              {property.languagesSpoken.join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Description */}
      {(descriptionText) && (
        <div className="mt-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
            {t('about', { name: property.name_en })}
          </h2>
          <div className="space-y-3">
            {descriptionText.split(/\n\n+/).filter(Boolean).map((paragraph, i) => (
              <p
                key={i}
                className="text-gray-600 dark:text-gray-400 leading-relaxed"
              >
                {paragraph.trim()}
              </p>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}
